<template>
  <div class="sales-step-content">
    <div class="text-center mb-3">
      <font-awesome-icon icon="coins" size="3x" class="text-warning mb-2" />
      <h4>Quittungspaket wählen</h4>
      <p class="text-muted mb-0">
        Wähle ein vordefiniertes Paket oder gib eine individuelle Anzahl ein
      </p>
    </div>

    <!-- Coupon Input -->
    <CouponInput
      :description="'Hast du einen Gutscheincode? Gib ihn hier ein, um Rabatte zu erhalten.'"
      :coupon-type="'coin_purchase'"
      @coupon-validated="onCouponValidated"
      @coupon-cleared="onCouponCleared"
    />

    <!-- Coin Packages -->
    <div class="row g-3 justify-content-center">
      <div v-for="packageItem in coinPackages" :key="packageItem.coins" class="col-md-6 col-lg-4">
        <div 
          class="card plan-card h-100" 
          :class="{ 'selected': selectedPackage?.coins === packageItem.coins }"
          @click="selectPackage(packageItem)"
        >
          <div class="card-body text-center">
            <div class="mb-3">
              <font-awesome-icon icon="coins" size="3x" class="text-warning" />
            </div>
            <h5 class="card-title">{{ packageItem.coins }} Quittungen</h5>
            <div class="h4 text-primary mb-2">{{ formatCurrency(packageItem.price) }}</div>
            <p class="text-muted small mb-3">
              {{ formatCurrency(packageItem.pricePerCoin) }} pro Quittung
            </p>
            
            <!-- Selection Indicator -->
            <div v-if="selectedPackage?.coins === packageItem.coins">
              <font-awesome-icon icon="check-circle" class="text-success sales-step-icon" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Amount Input -->
    <div class="card mb-3">
      <div class="card-header py-2">
        <h6 class="mb-0">
          <font-awesome-icon icon="edit" class="me-2" />
          Individuelle Anzahl
        </h6>
      </div>
      <div class="card-body p-3">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Anzahl Quittungen</label>
            <input
              v-model.number="customCoins"
              type="number"
              class="form-control"
              placeholder="z.B. 50"
              min="1"
              @input="calculateCustomPrice"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">Preis pro Quittung</label>
            <div class="form-control-plaintext">
              <strong>{{ formatCurrency(dynamicPricePerCoin) }}</strong>
            </div>
          </div>
        </div>
        <div v-if="customCoins > 0" class="mt-3">
          <div class="alert alert-info">
            <div class="row">
              <div class="col-6"><strong>Gesamtpreis:</strong></div>
              <div class="col-6 text-end"><strong>{{ formatCurrency(customTotalPrice) }}</strong></div>
            </div>
            <div class="row">
              <div class="col-6"><strong>Netto:</strong></div>
              <div class="col-6 text-end">{{ formatCurrency(customNetPrice) }}</div>
            </div>
            <div class="row">
              <div class="col-6"><strong>MwSt. (19%):</strong></div>
              <div class="col-6 text-end">{{ formatCurrency(customVatAmount) }}</div>
            </div>
          </div>
          <div class="text-center">
            <button 
              class="btn btn-primary"
              @click="selectCustomPackage"
              :disabled="customCoins <= 0"
            >
              <font-awesome-icon icon="check" class="me-2" />
              {{ customCoins }} Quittungen für {{ formatCurrency(customTotalPrice) }} auswählen
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { usePricing } from '../composables/usePricing'
import CouponInput from '../components/CouponInput.vue'

const props = defineProps({
  coinPackages: {
    type: Array,
    default: () => []
  },
  selectedPackage: {
    type: Object,
    default: null
  },
  couponResult: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['package-selected', 'coupon-validated', 'coupon-cleared'])

const { formatCurrency, calculateCustomCoinPrice } = usePricing()

const customCoins = ref(0)
const customTotalPrice = ref(0)
const customNetPrice = ref(0)
const customVatAmount = ref(0)
const dynamicPricePerCoin = ref(0.49)

const selectPackage = (packageItem) => {
  emit('package-selected', packageItem)
}

const selectCustomPackage = () => {
  const customPackage = {
    coins: customCoins.value,
    price: customTotalPrice.value,
    pricePerCoin: dynamicPricePerCoin.value,
    isCustom: true
  }
  emit('package-selected', customPackage)
}

const calculateCustomPrice = async () => {
  if (customCoins.value > 0) {
    try {
      const priceData = await calculateCustomCoinPrice(customCoins.value)
      
      customNetPrice.value = priceData.netPrice
      customVatAmount.value = priceData.vatAmount
      customTotalPrice.value = priceData.totalPrice
      dynamicPricePerCoin.value = priceData.pricePerCoin
    } catch (error) {
      console.error('Fehler bei der dynamischen Preisberechnung:', error)
      // Fallback to static calculation
      dynamicPricePerCoin.value = 0.142
      customNetPrice.value = Math.round((customCoins.value * dynamicPricePerCoin.value) / 1.19 * 100) / 100
      customVatAmount.value = Math.round((customCoins.value * dynamicPricePerCoin.value - customNetPrice.value) * 100) / 100
      customTotalPrice.value = Math.round(customCoins.value * dynamicPricePerCoin.value * 100) / 100
    }
  }
}

const onCouponValidated = (result) => {
  emit('coupon-validated', result)
}

const onCouponCleared = () => {
  emit('coupon-cleared')
}
</script>

<style scoped>
.plan-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.plan-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border-color: rgba(13, 110, 253, 0.2);
}

.plan-card.selected {
  border-color: var(--bs-primary);
  background: rgba(13, 110, 253, 0.05);
}

.sales-step-icon {
  font-size: 16px;
}
</style>
